/**
 * Track Topic Page — עמוד נושא
 *
 * Structured lesson → button → free chat scoped to the topic. The chat mode is
 * reflected in the URL (?mode=chat) so back/refresh behave the way a reader
 * expects.
 *
 * Reading the lesson costs nothing; only the chat consumes the AI allowance.
 */

import { useEffect, useRef } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AlertCircle, ArrowRight, BookOpen, Check, MessageCircle } from 'lucide-react'
import LessonView from '../../components/learning/LessonView'
import TopicChatPanel from '../../components/learning/TopicChatPanel'
import { useLearningStore } from '../../store/learningStore'
import { TRACKS, type TrackKey } from '../../config/tracks'

interface TrackTopicPageProps {
  track: TrackKey
}

export default function TrackTopicPage({ track }: TrackTopicPageProps) {
  const { slug = '' } = useParams<{ slug: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const config = TRACKS[track]

  const {
    topicDetail,
    topicDetailLoading,
    topicDetailError,
    topics,
    fetchTopic,
    loadTopicConversation,
    completeTopic,
    clearTopic,
  } = useLearningStore()

  const chatMode = searchParams.get('mode') === 'chat'
  const tileState = topics.find((t) => t.slug === slug)
  const isCompleted = tileState?.status === 'completed'

  useEffect(() => {
    clearTopic()
    void fetchTopic(track, slug)
    void loadTopicConversation(track, slug)
  }, [track, slug, fetchTopic, loadTopicConversation, clearTopic])

  useEffect(() => {
    if (topicDetail) headingRef.current?.focus()
  }, [topicDetail])

  const handleComplete = async () => {
    try {
      await completeTopic(track, slug)
      navigate(config.basePath)
    } catch {
      // completeTopic surfaces the message through the store's error state.
    }
  }

  if (topicDetailLoading) {
    return (
      <p className="mx-auto max-w-3xl px-4 py-10 text-center text-sm text-gray-600">
        טוען את השיעור...
      </p>
    )
  }

  if (topicDetailError || !topicDetail) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div
          className="flex items-center gap-2 rounded-xl bg-red-50 p-4 text-sm text-red-800"
          role="alert"
        >
          <AlertCircle size={18} aria-hidden="true" />
          {topicDetailError || 'הנושא לא נמצא'}
        </div>
        <Link to={config.basePath} className="btn btn-secondary mt-4 inline-flex">
          חזרה ל{config.name}
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6">
      <nav aria-label="ניווט חזרה">
        <Link
          to={config.basePath}
          className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline"
        >
          <ArrowRight size={16} aria-hidden="true" />
          חזרה ל{config.name}
        </Link>
      </nav>

      <header className="mt-4">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-2xl font-bold text-gray-900 focus-visible:outline-none"
        >
          {topicDetail.title}
        </h1>
        {topicDetail.subtitle && (
          <p className="mt-1 text-sm text-gray-700">{topicDetail.subtitle}</p>
        )}
        {isCompleted && (
          <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800">
            <Check size={13} aria-hidden="true" />
            הושלם
          </p>
        )}
      </header>

      {/* Mode switch: lesson ⇄ chat */}
      <div
        className="mt-6 flex gap-2 border-b border-gray-200"
        role="tablist"
        aria-label="תצוגת הנושא"
      >
        <button
          type="button"
          role="tab"
          aria-selected={!chatMode}
          onClick={() => setSearchParams({})}
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
            !chatMode
              ? 'border-primary-600 text-primary-800'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <BookOpen size={15} aria-hidden="true" className="ms-1 inline" />
          השיעור
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={chatMode}
          onClick={() => setSearchParams({ mode: 'chat' })}
          className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
            chatMode
              ? 'border-primary-600 text-primary-800'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <MessageCircle size={15} aria-hidden="true" className="ms-1 inline" />
          שאלות
        </button>
      </div>

      <div className="mt-6">
        {chatMode ? (
          <TopicChatPanel
            track={track}
            topicSlug={slug}
            topicTitle={topicDetail.title}
            suggestedQuestions={topicDetail.suggestedQuestions}
          />
        ) : (
          <>
            <LessonView lessonHtml={topicDetail.lessonHtml} keyTerms={topicDetail.keyTerms} />

            <div className="mt-8 flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-800">יש משהו שלא הובן? אפשר לשאול.</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSearchParams({ mode: 'chat' })}
                  className="btn btn-primary"
                >
                  <MessageCircle size={16} aria-hidden="true" className="ms-1" />
                  יש לי שאלות
                </button>
                <button type="button" onClick={handleComplete} className="btn btn-secondary">
                  <Check size={16} aria-hidden="true" className="ms-1" />
                  {isCompleted ? 'סומן כהושלם' : 'סמן כהושלם'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
